<template>
  <!-- Main Wrapper -->
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <!-- Loading State -->
        <div v-if="pageLoading" class="edit-loading">
          <div class="card"><div class="card-body">
            <div class="skeleton-line w-40 mb-3"></div>
            <div class="row g-3">
              <div class="col-lg-4"><div class="skeleton-line"></div></div>
              <div class="col-lg-4"><div class="skeleton-line"></div></div>
              <div class="col-lg-4"><div class="skeleton-line"></div></div>
            </div>
          </div></div>
        </div>

        <!-- Edit Form -->
        <div v-else class="card">
          <div class="card-body">

            <!-- ══════════════════════════════════════
                 SECTION 1 · BASIC INFORMATION
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-box"></i></div>
              <div>
                <h6 class="section-title">Basic Information</h6>
                <p class="section-sub">Core product identity details</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <!-- Category -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Category <span class="required-star">*</span></label>
                  <model-list-select
                    :list="categoryListFromApi"
                    v-model="form.itemcategory"
                    option-value="categoryid"
                    option-text="categoryname"
                    placeholder="Select category"
                    :class="{ 'is-invalid': errors.itemcategory }"
                  />
                  <div class="invalid-feedback" v-if="errors.itemcategory">{{ errors.itemcategory }}</div>
                </div>
              </div>

              <!-- Item Name -->
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Item Name <span class="required-star">*</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.itemname"
                    ref="itemNameInput"
                    placeholder="e.g. Tata Tea Premium 500g"
                    :class="{ 'is-invalid': errors.itemname }"
                  />
                  <div class="invalid-feedback" v-if="errors.itemname">{{ errors.itemname }}</div>
                </div>
              </div>

              <!-- Item Code -->
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Item Code <span class="required-star">*</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.itemcode"
                    placeholder="e.g. IT-0001"
                    readonly
                    :class="{ 'is-invalid': errors.itemcode }"
                  />
                  <small class="form-hint">Item code cannot be changed after creation</small>
                  <div class="invalid-feedback" v-if="errors.itemcode">{{ errors.itemcode }}</div>
                </div>
              </div>

              <!-- Barcode -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Barcode <span class="optional-tag">Optional</span></label>
                  <div class="input-with-icon">
                    <i class="fas fa-barcode input-icon"></i>
                    <b-form-input
                      type="text"
                      v-model="form.barcode"
                      placeholder="Scan or enter barcode"
                    />
                  </div>
                </div>
              </div>

              <!-- Rack No -->
              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Rack No <span class="optional-tag">Optional</span></label>
                  <b-form-input
                    type="text"
                    v-model="form.rackno"
                    placeholder="e.g. A1, Rack-02"
                  />
                </div>
              </div>

              <!-- Item Description -->
              <div class="col-lg-9">
                <div class="form-group">
                  <label class="form-label">Description <span class="optional-tag">Optional</span></label>
                  <b-form-textarea
                    v-model="form.itemdescription"
                    placeholder="Brief description of the item..."
                    rows="2"
                    max-rows="4"
                  />
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 2 · PRICING
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-rupee-sign"></i></div>
              <div>
                <h6 class="section-title">Pricing</h6>
                <p class="section-sub">Purchase, selling and wholesale rates</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Purchase Rate <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input
                      type="number" min="0" step="0.01"
                      v-model="form.purchaserate"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">MRP <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input type="number" min="0" step="0.01" v-model="form.mrp" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Selling Price <span class="required-star">*</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input
                      type="number" min="0" step="0.01"
                      v-model="form.unitprice"
                      placeholder="0.00"
                      :class="{ 'is-invalid': errors.unitprice }"
                    />
                  </div>
                  <div class="invalid-feedback" v-if="errors.unitprice">{{ errors.unitprice }}</div>
                </div>
              </div>

              <div class="col-lg-3 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Wholesale Rate <span class="optional-tag">Optional</span></label>
                  <div class="input-currency">
                    <span class="currency-symbol">₹</span>
                    <b-form-input type="number" min="0" step="0.01" v-model="form.wholesalerate" placeholder="0.00" />
                  </div>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 3 · STOCK & UNIT
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-warehouse"></i></div>
              <div>
                <h6 class="section-title">Stock & Unit</h6>
                <p class="section-sub">Inventory levels and measurement unit</p>
              </div>
            </div>

            <div class="row g-3 mb-4">

              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Unit <span class="required-star">*</span></label>
                  <div class="unit-field-row">
                    <button
                      type="button"
                      class="unit-add-btn"
                      title="Add new unit"
                      @click="openUnitModal"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                    <model-list-select
                      :list="unitListFromApi"
                      v-model="form.unit"
                      option-value="unitname"
                      option-text="unitname"
                      placeholder="Select unit"
                      :class="{ 'is-invalid': errors.unit }"
                    />
                  </div>
                  <div class="invalid-feedback" v-if="errors.unit">{{ errors.unit }}</div>
                </div>
              </div>

              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Available Stock <span class="required-star">*</span></label>
                  <b-form-input
                    type="number" min="0" step="0.01"
                    v-model="form.available"
                    placeholder="0"
                    readonly
                  />
                  <small class="form-hint stock-warning-hint">Available stock is updated from purchase, sales, and returns only</small>
                </div>
              </div>

              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label class="form-label">Min Stock <span class="required-star">*</span></label>
                  <b-form-input type="number" min="0" v-model="form.minstock" placeholder="e.g. 5" :class="{ 'is-invalid': errors.minstock }" />
                  <small class="form-hint">Low stock alert will use this value</small>
                  <div class="invalid-feedback" v-if="errors.minstock">{{ errors.minstock }}</div>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 4 · TAX DETAILS
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-percent"></i></div>
              <div>
                <h6 class="section-title">Tax Details</h6>
                <p class="section-sub">GST and HSN classification</p>
              </div>
            </div>

            <div class="tax-panel mb-4">

              <div class="row g-3 mb-3">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">{{ form.taxtype === 'SAC' ? 'SAC Code' : 'HSN Code' }} <span v-if="isTaxableType && !form.is_manual_tax_rate" class="required-star">*</span></label>
                    <b-form-input type="text" v-model.trim="form.hsncode" :disabled="form.is_manual_tax_rate || !isTaxableType" placeholder="e.g. 09024090" :class="{ 'is-invalid': errors.hsncode }" @blur="resolveTaxRateForCode" @keyup.enter="resolveTaxRateForCode" />
                    <small class="form-hint">Enter a code from HSN Tax Master to auto-fill the tax rate</small>
                    <div v-if="taxRateLookup.status" class="tax-rate-status" :class="taxRateLookup.status">
                      {{ taxRateLookup.message }}
                    </div>
                    <div class="invalid-feedback" v-if="errors.hsncode">{{ errors.hsncode }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">Tax Type <span class="required-star">*</span></label>
                    <b-form-select
                      v-model="form.taxtype"
                      :options="taxTypeOptions"
                      @change="handleTaxTypeChange"
                    />
                    <small class="form-hint">Nature of tax applicable to this item</small>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">Tax Inclusive?</label>
                    <div class="form-check form-switch mt-2">
                      <b-form-checkbox
                        v-model="form.istaxinclusive"
                        switch
                      >
                        Price includes tax
                      </b-form-checkbox>
                    </div>
                    <small class="form-hint">MRP-based pricing (tax included) vs Base price (tax added)</small>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12" v-if="isTaxableType">
                  <div class="form-group mb-0">
                    <label class="form-label">Manual Tax Rate?</label>
                    <b-form-checkbox v-model="form.is_manual_tax_rate" switch @change="handleManualTaxToggle">
                      Enter rate manually
                    </b-form-checkbox>
                    <small class="form-hint">Use only when the code is not in Tax Rates</small>
                  </div>
                </div>
              </div>

              <!-- GST Rate Selector -->
              <div class="row g-0 mb-3" v-if="isTaxableType && form.is_manual_tax_rate">
                <div class="col-12">
                  <label class="form-label mb-2">Tax Rate <span class="required-star">*</span></label>
                  <div class="gst-rate-selector">
                    <button
                      v-for="rate in gstRates"
                      :key="rate.label"
                      type="button"
                      class="gst-rate-btn"
                      :class="{ active: selectedGstRate === rate.label }"
                      @click="applyGstRate(rate)"
                    >
                      <span class="rate-label">{{ rate.label }}</span>
                      <span class="rate-breakdown" v-if="rate.total > 0">CGST {{ rate.cgst }}% + SGST {{ rate.sgst }}% + IGST {{ rate.total }}%</span>
                      <span class="rate-breakdown" v-else>Nil rated</span>
                    </button>
                    <button
                      type="button"
                      class="gst-rate-btn"
                      :class="{ active: selectedGstRate === 'custom' }"
                      @click="selectedGstRate = 'custom'"
                    >
                      <span class="rate-label">Custom</span>
                      <span class="rate-breakdown">Enter manually</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Live GST Breakdown -->
              <div class="gst-breakdown-row" v-if="isTaxableType && form.is_manual_tax_rate">
                <div class="gst-field">
                  <label class="form-label">Total Tax %</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="100" step="0.01"
                      v-model="form.taxpercent"
                      placeholder="0.00"
                      @input="syncTaxFromTotal"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-divider-arrow">
                  <i class="fas fa-arrow-right"></i>
                  <span>Split equally</span>
                </div>
                <div class="gst-field">
                  <label class="form-label">CGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="50" step="0.01"
                      v-model="form.cgstrate"
                      placeholder="0.00"
                      @input="syncTaxFromCgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-plus">+</div>
                <div class="gst-field">
                  <label class="form-label">SGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="50" step="0.01"
                      v-model="form.sgstrate"
                      placeholder="0.00"
                      @input="syncTaxFromSgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-plus">+</div>
                <div class="gst-field">
                  <label class="form-label">IGST</label>
                  <div class="input-with-suffix">
                    <b-form-input
                      type="number" min="0" max="100" step="0.01"
                      v-model="form.igstrate"
                      placeholder="0.00"
                      @input="syncTaxFromIgst"
                      :readonly="selectedGstRate !== 'custom'"
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
                <div class="gst-preview" v-if="form.unitprice > 0 && form.taxpercent > 0">
                  <div class="preview-label">
                    {{ form.istaxinclusive ? 'Tax Component' : 'Price + Tax' }}
                  </div>
                  <div class="preview-value">₹{{ priceWithTax }}</div>
                  <div class="preview-hint" v-if="form.istaxinclusive">
                    Customer pays ₹{{ form.unitprice }} (tax included)
                  </div>
                </div>
              </div>

              <div class="tax-type-message mt-3" v-if="form.taxtype !== 'GST'">
                <div class="alert alert-info">
                  <i class="fas fa-info-circle"></i>
                  <span v-if="form.taxtype === 'EXEMPT'">
                    This item is tax-exempt under GST. No tax will be charged.
                  </span>
                  <span v-else-if="form.taxtype === 'SAC'">
                    This is a service item (SAC code). GST will be applied as per service tax rules.
                  </span>
                  <span v-else-if="form.taxtype === 'NONGST'">
                    This item is outside GST scope. No GST will be applied.
                  </span>
                </div>
              </div>

            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 5 · UNIT OF MEASUREMENT
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-ruler-combined"></i></div>
              <div>
                <h6 class="section-title">Unit of Measurement (UOM)</h6>
                <p class="section-sub">Define how this item is packed and measured</p>
              </div>
            </div>

            <div class="uom-panel mb-4">
              <div class="row g-3 align-items-end">

                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <span class="uom-badge-inline upper">UPPER UNIT</span>
                      Unit Name <span class="optional-tag">Optional</span>
                    </label>
                    <b-form-input
                      type="text"
                      v-model="form.uom_upper"
                      placeholder="e.g. Box, Carton, Case"
                    />
                  </div>
                </div>

                <div class="col-lg-4 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <i class="fas fa-exchange-alt me-1" style="color:#3d5af1;"></i>
                      Conversion Factor
                    </label>
                    <div class="uom-conversion-row">
                      <span class="uom-chip upper">{{ form.uom_upper || 'Upper' }}</span>
                      <span class="uom-equals">=</span>
                      <b-form-input
                        type="number"
                        min="1"
                        step="1"
                        v-model="form.conversionfactor"
                        placeholder="1"
                        class="uom-factor-input"
                      />
                      <span class="uom-chip lower">{{ form.uom_lower || 'Lower' }}</span>
                    </div>
                    <small class="form-hint">How many lower units fit in one upper unit</small>
                  </div>
                </div>

                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group mb-0">
                    <label class="form-label">
                      <span class="uom-badge-inline lower">LOWER UNIT</span>
                      Unit Name <span class="optional-tag">Optional</span>
                    </label>
                    <b-form-input
                      type="text"
                      v-model="form.uom_lower"
                      placeholder="e.g. Piece, Packet, Bottle"
                    />
                  </div>
                </div>

                <div class="col-lg-2 col-sm-12 col-12" v-if="form.uom_upper && form.uom_lower && form.conversionfactor">
                  <div class="uom-example-card">
                    <i class="fas fa-lightbulb me-1"></i>
                    1 {{ form.uom_upper }} = {{ form.conversionfactor }} {{ form.uom_lower }}
                  </div>
                </div>

              </div>
            </div>

            <div class="section-divider"></div>

            <!-- ══════════════════════════════════════
                 SECTION 6 · ITEM IMAGES
                 ⚠️  FEATURE DISABLED — Image upload
                 will be enabled in a future release.
                 Uncomment this section and the
                 localImages / maxImages / uploadError /
                 imagesToDelete data fields + all image
                 methods to re-enable.
            ══════════════════════════════════════

            <div class="section-header">
              <div class="section-icon"><i class="fas fa-images"></i></div>
              <div>
                <h6 class="section-title">Item Images</h6>
                <p class="section-sub">Optional — up to 4 images</p>
              </div>
            </div>

            <div class="mb-4">
              <input
                type="file"
                accept="image/*"
                multiple
                @change="handleImageUpload"
                ref="imageUploader"
                style="display: none;"
              />
              <div class="image-grid">
                <div
                  v-for="(image, index) in localImages"
                  :key="image.url || (image.file && image.file.name)"
                  class="image-tile"
                >
                  <img :src="image.url" alt="Item Image" />
                  <button type="button" class="image-remove" @click="removeLocalImage(index)">
                    <i class="fas fa-times"></i>
                  </button>
                  <span
                    class="image-status-label"
                    :class="image.isNew ? 'bg-info' : 'bg-success'"
                  >{{ image.isNew ? 'New' : 'Saved' }}</span>
                </div>
                <div
                  class="image-add-tile"
                  @click="$refs.imageUploader.click()"
                  v-if="localImages.length < maxImages"
                >
                  <i class="fas fa-plus"></i>
                  <span>Add Photo</span>
                </div>
              </div>
              <small class="text-danger d-block mt-1" v-if="uploadError">{{ uploadError }}</small>
              <small class="form-hint" v-else>JPG, PNG, WEBP supported · Max 4 images · Green = saved on server, Blue = pending upload</small>
            </div>

            <div class="section-divider"></div>
            -->

            <!-- ══════════════════════════════════════
                 ACTION BUTTONS
            ══════════════════════════════════════ -->
            <div class="section-header">
              <div class="section-icon"><i class="fas fa-images"></i></div>
              <div>
                <h6 class="section-title">Item Images</h6>
                <p class="section-sub">Optional product photos</p>
              </div>
            </div>

            <div class="mb-4">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                @change="handleImageUpload"
                ref="imageUploader"
                style="display: none;"
              />
              <div class="image-grid">
                <div
                  v-for="(image, index) in localImages"
                  :key="image.image_key || image.url"
                  class="image-tile"
                >
                  <img :src="image.url" alt="Item Image" />
                  <button type="button" class="image-remove" @click="removeLocalImage(index)">
                    <i class="fas fa-times"></i>
                  </button>
                  <span class="image-status-label" :class="image.isNew ? 'bg-info' : 'bg-success'">
                    {{ image.isNew ? 'New' : 'Saved' }}
                  </span>
                </div>
                <div
                  class="image-add-tile"
                  @click="$refs.imageUploader.click()"
                  v-if="localImages.length < maxImages"
                >
                  <i class="fas fa-plus"></i>
                  <span>Add Photo</span>
                </div>
              </div>
              <small class="text-danger d-block mt-1" v-if="uploadError">{{ uploadError }}</small>
              <small class="form-hint" v-else>JPG, PNG, WEBP supported. Max 2 MB per image.</small>
            </div>

            <div class="section-divider"></div>

            <div v-if="isEcommerceStoreEnabled">
              <div class="section-header">
                <div class="section-icon"><i class="fas fa-store"></i></div>
                <div>
                  <h6 class="section-title">Ecommerce Settings</h6>
                  <p class="section-sub">Online product catalogue details</p>
                </div>
              </div>

              <div class="row g-3 mb-4">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Publish Online</label>
                    <b-form-checkbox v-model="ecommerceForm.is_online" switch>
                      {{ ecommerceForm.is_online ? 'Published' : 'Draft' }}
                    </b-form-checkbox>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label class="form-label">Online Price</label>
                    <div class="input-currency">
                      <span class="currency-symbol">₹</span>
                      <b-form-input type="number" min="0" step="0.01" v-model="ecommerceForm.online_price" />
                    </div>
                  </div>
                </div>
                <div class="col-lg-6 col-sm-12">
                  <div class="form-group">
                    <label class="form-label">Slug</label>
                    <b-form-input v-model="ecommerceForm.slug" placeholder="red-notebook-a4" />
                  </div>
                </div>
                <div class="col-lg-6 col-sm-12">
                  <div class="form-group">
                    <label class="form-label">Short Description</label>
                    <b-form-input v-model="ecommerceForm.short_description" />
                  </div>
                </div>
                <div class="col-lg-6 col-sm-12">
                  <div class="form-group">
                    <label class="form-label">SEO Title</label>
                    <b-form-input v-model="ecommerceForm.seo_title" />
                  </div>
                </div>
                <div class="col-lg-6 col-sm-12">
                  <div class="form-group">
                    <label class="form-label">SEO Description</label>
                    <b-form-input v-model="ecommerceForm.seo_description" />
                  </div>
                </div>
                <div class="col-lg-6 col-sm-12">
                  <div class="form-group">
                    <label class="form-label">Ecommerce Categories</label>
                    <select v-model="ecommerceForm.category_ids" class="form-control ecommerce-category-select" multiple>
                      <option v-for="category in ecommerceCategories" :key="category.ecommerce_category_id" :value="category.ecommerce_category_id">
                        {{ category.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-group">
                    <label class="form-label">Long Description</label>
                    <b-form-textarea v-model="ecommerceForm.long_description" rows="3" />
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label">Images</label>
                  <div class="image-grid" v-if="ecommerceImages.length">
                    <div v-for="image in ecommerceImages" :key="image.image_id || image.url" class="image-tile">
                      <img :src="image.preview_url || image.url" :alt="image.alt_text || 'Item image'" />
                      <span class="image-status-label bg-success">{{ image.is_primary ? 'Primary' : 'Image' }}</span>
                    </div>
                  </div>
                  <div v-else class="form-hint">No item images found.</div>
                </div>
              </div>

              <div class="form-actions mb-4">
                <button type="button" class="btn btn-submit" @click="saveOnlineProduct">
                  <i class="fas fa-cloud-upload-alt me-2"></i>Save Online Product
                </button>
                <button type="button" class="btn btn-cancel" @click="removeFromOnlineStore">
                  <i class="fas fa-store-slash me-2"></i>Remove From Online Store
                </button>
              </div>
              <div class="section-divider"></div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-submit" @click="postData()">
                <i class="fas fa-save me-2"></i>Save Changes
              </button>
              <router-link to="/productlist" class="btn btn-cancel">
                <i class="fas fa-times me-2"></i>Cancel
              </router-link>
            </div>

          </div>
        </div>

      </div>
    </div>

    <div
      v-if="showUnitModal"
      class="quick-create-modal-backdrop"
      @click.self="closeUnitModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="unit-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="unit-quick-create-title" class="quick-create-modal-title">Add New Unit</h5>
            <p class="quick-create-modal-subtitle">The unit will be available immediately in this form.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeUnitModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Unit Name <span class="required-star">*</span></label>
            <b-form-input
              ref="unitModalNameInput"
              v-model="unitModalForm.unitname"
              type="text"
              placeholder="e.g. Nos, Kg, Box"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Unit Code</label>
            <b-form-input
              v-model="unitModalForm.unitcode"
              type="text"
              placeholder="Optional short code"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Description</label>
            <b-form-textarea
              v-model="unitModalForm.description"
              rows="2"
              placeholder="Optional description"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeUnitModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="unitModalSaving" @click="createUnitFromModal">
            {{ unitModalSaving ? "Saving..." : "Save Unit" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from "vue-search-select";
import axios from "axios";

// ── Image API helpers (kept for future re-enable) ──────────
// async function ImageUploadApi(item_id, files) { ... }
// async function ImageDeleteApi(itemId, url) { ... }

export default {
  name: "editproduct",
  components: { ModelListSelect },

  data() {
    return {
      title: "Edit Item",
      title1: "Update an existing item",

      id: null,
      pageLoading: true,
      errors: {},

      form: {
        // Basic
        itemname: "",
        itemdescription: "",
        itemcategory: "",
        itemcode: "",
        barcode: "",
        rackno: "",

        // Pricing
        unitprice: "",
        purchaserate: "",
        mrp: "",
        wholesalerate: "",

        // Stock
        unit: "",
        available: "",
        threshold: "",
        minstock: "",
        maxstock: "",

        // Tax
        hsncode: "",
        taxpercent: "",
        cgstrate: "",
        sgstrate: "",
        igstrate: "",
        is_manual_tax_rate: false,
        taxtype: "GST",
        istaxinclusive: false,

        // UOM
        uom_upper: "",
        uom_lower: "",
        conversionfactor: "",

        // ── TRACKING OPTIONS (disabled — future feature) ──
        // batchenabled: false,
        // expiryenabled: false,
      },

      // ── IMAGE UPLOAD (disabled — future feature) ──
      localImages: [],
      imagesToDelete: [],
      maxImages: 4,
      uploadError: "",

      categoryListFromApi: [],
      ecommerceCategories: [],
      ecommerceImages: [],
      ecommerceForm: {
        is_online: false,
        online_price: "",
        slug: "",
        short_description: "",
        long_description: "",
        seo_title: "",
        seo_description: "",
        category_ids: [],
      },
      unitListFromApi: [],
      showUnitModal: false,
      unitModalSaving: false,
      unitModalForm: {
        unitname: "",
        unitcode: "",
        description: "",
      },

      taxTypeOptions: [
        { value: "GST", text: "GST (Goods)" },
        { value: "SAC", text: "SAC (Services)" },
        { value: "EXEMPT", text: "Exempt" },
        { value: "NONGST", text: "Non-GST" }
      ],

      gstRates: [
        { label: "0%",  total: 0,  cgst: 0,   sgst: 0,   igst: 0   },
        { label: "5%",  total: 5,  cgst: 2.5, sgst: 2.5, igst: 5   },
        { label: "12%", total: 12, cgst: 6,   sgst: 6,   igst: 12  },
        { label: "18%", total: 18, cgst: 9,   sgst: 9,   igst: 18  },
        { label: "28%", total: 28, cgst: 14,  sgst: 14,  igst: 28  },
      ],
      selectedGstRate: null,
      taxRateLookup: {
        status: "",
        message: "",
      },
    };
  },

  computed: {
    isEcommerceStoreEnabled() {
      return this.$store.getters.isEcommerceStoreEnabled;
    },
    isTaxableType() {
      return ["GST", "SAC"].includes(this.form.taxtype);
    },
    priceWithTax() {
      const price = parseFloat(this.form.unitprice) || 0;
      const tax   = parseFloat(this.form.taxpercent) || 0;

      if (this.form.istaxinclusive) {
        const taxAmount = (price * tax) / (100 + tax);
        return taxAmount.toFixed(2);
      }

      return (price + (price * tax) / 100).toFixed(2);
    },

    // ── IMAGE UPLOAD (disabled — future feature) ──
    // newFilesToUpload() {
    //   return this.localImages.filter(img => img.isNew).map(img => img.file);
    // },
  },

  created() {
    this.fetchCategoryListFromApi();
    this.fetchEcommerceCategories();
    this.fetchUnitListFromApi();
  },

  beforeMount() {
    this.id = this.$route.query.id;
    this.getProductById(this.id);
  },

  methods: {
    // ── Category fetch ─────────────────────────────────
    async fetchCategoryListFromApi() {
      try {
        const response = await axios.get("/v1/category");
        this.categoryListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching category list:", error);
      }
    },

    async fetchUnitListFromApi() {
      try {
        const response = await axios.get("/v1/units");
        this.unitListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching unit list:", error);
      }
    },

    async fetchEcommerceCategories() {
      if (!this.$store.getters.isEcommerceStoreEnabled) return;
      try {
        const response = await axios.get("/v1/ecommerce-categories");
        this.ecommerceCategories = response.data || [];
      } catch (error) {
        console.error("Error fetching ecommerce categories:", error);
      }
    },

    getEmptyUnitModalForm() {
      return {
        unitname: "",
        unitcode: "",
        description: "",
      };
    },

    openUnitModal() {
      this.unitModalForm = this.getEmptyUnitModalForm();
      this.showUnitModal = true;
      this.$nextTick(() => {
        const input = this.$refs.unitModalNameInput;
        input?.focus?.();
      });
    },

    closeUnitModal() {
      this.showUnitModal = false;
      this.unitModalSaving = false;
      this.unitModalForm = this.getEmptyUnitModalForm();
    },

    async createUnitFromModal() {
      const unitname = this.unitModalForm.unitname?.trim();
      if (!unitname) {
        Swal.fire({ icon: "warning", title: "Unit name required", text: "Please enter a unit name." });
        return;
      }

      this.unitModalSaving = true;
      try {
        const payload = {
          unitname,
          unitcode: this.unitModalForm.unitcode?.trim() || null,
          description: this.unitModalForm.description?.trim() || null,
        };
        const response = await axios.post("/v1/units", payload);
        await this.fetchUnitListFromApi();
        this.form.unit = response.data?.unitname || unitname;
        this.errors.unit = "";
        this.closeUnitModal();
        Swal.fire({
          icon: "success",
          title: "Unit added",
          text: `"${unitname}" is ready to use.`,
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not add unit",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.unitModalSaving = false;
      }
    },

    // ── Load existing item ─────────────────────────────
    async getProductById(id) {
      this.pageLoading = true;
      try {
        const response = await axios.get("/v1/items/ecommerce/" + id);
        const data = response.data;

        // Map API response fields to form — only the fields in our table
        this.form = {
          itemname:         data.itemname         || "",
          itemdescription:  data.itemdescription  || "",
          itemcategory:     data.itemcategory      || "",
          itemcode:         data.itemcode          || "",
          barcode:          data.barcode           || "",
          rackno:           data.rackno            || "",

          unitprice:        data.unitprice         ?? "",
          purchaserate:     data.purchaserate      ?? "",
          mrp:              data.mrp               ?? "",
          wholesalerate:    data.wholesalerate     ?? "",

          unit:             data.unit              || "",
          available:        data.available         ?? "",
          threshold:        data.threshold         ?? "",
          minstock:         data.minstock ?? data.threshold ?? "",
          maxstock:         data.maxstock          ?? "",

          hsncode:          data.hsncode           || "",
          taxpercent:       data.taxpercent        ?? "",
          cgstrate:         data.cgstrate          ?? "",
          sgstrate:         data.sgstrate          ?? "",
          igstrate:         data.igstrate          ?? "",
          is_manual_tax_rate: Boolean(data.is_manual_tax_rate),
          taxtype:          data.taxtype           || "GST",
          istaxinclusive:   Boolean(data.istaxinclusive),

          uom_upper:        data.uom_upper         || "",
          uom_lower:        data.uom_lower         || "",
          conversionfactor: data.conversionfactor  ?? "",
        };

        const ecommerce = data.ecommerce || {};
        this.ecommerceForm = {
          is_online: Boolean(data.is_online || ecommerce.is_active),
          online_price: data.online_price ?? "",
          slug: data.slug || ecommerce.slug || this.slugify(data.itemname || ""),
          short_description: ecommerce.short_description || data.itemdescription || "",
          long_description: ecommerce.long_description || data.online_desc || "",
          seo_title: ecommerce.seo_title || data.itemname || "",
          seo_description: ecommerce.seo_description || "",
          category_ids: (ecommerce.categories || []).map((category) => category.ecommerce_category_id),
        };
        this.ecommerceImages = Array.isArray(ecommerce.images) ? ecommerce.images : [];

        // Pre-select GST rate button if it matches a slab
        this.preselectGstRate();
        if (this.form.hsncode && this.isTaxableType && !this.form.is_manual_tax_rate) {
          this.taxRateLookup = {
            status: "found",
            message: `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} ${this.form.hsncode} currently uses ${this.form.taxpercent}% tax.`,
          };
        }

        await this.loadExistingItemImages(id);

      } catch (error) {
        console.error("Error loading item:", error);
        Swal.fire({
          title: "Error loading item",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      } finally {
        this.pageLoading = false;
      }
    },

    // ── Pre-select GST slab after load ─────────────────
    preselectGstRate() {
      if (!this.isTaxableType) {
        this.selectedGstRate = null;
        return;
      }
      const tax = parseFloat(this.form.taxpercent);
      if (isNaN(tax)) return;
      const match = this.gstRates.find((r) => r.total === tax);
      this.selectedGstRate = match ? match.label : "custom";
    },

    handleTaxTypeChange() {
      if (!this.isTaxableType) {
        this.form.hsncode = "";
        this.form.is_manual_tax_rate = false;
        this.form.taxpercent = "0";
        this.form.cgstrate = "0";
        this.form.sgstrate = "0";
        this.form.igstrate = "0";
        this.selectedGstRate = null;
        this.clearTaxRateLookup();
      } else if (this.form.hsncode && !this.form.is_manual_tax_rate) {
        this.resolveTaxRateForCode();
      }
    },
    handleManualTaxToggle() {
      if (this.form.is_manual_tax_rate) {
        this.form.hsncode = "";
        this.selectedGstRate = "custom";
        this.clearTaxRateLookup();
      } else {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
      }
    },

    clearTaxRateLookup() {
      this.taxRateLookup = { status: "", message: "" };
    },

    async resolveTaxRateForCode() {
      if (!this.isTaxableType || this.form.is_manual_tax_rate) return;
      const code = String(this.form.hsncode || "").trim();
      if (!code) {
        this.clearTaxRateLookup();
        return;
      }

      this.taxRateLookup = { status: "loading", message: "Checking HSN Tax Master..." };
      try {
        const response = await axios.get("/v1/tax/rates/resolve", {
          params: {
            code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
            code,
          },
        });
        const rate = response.data || {};
        this.form.taxpercent = String(rate.tax_percent ?? "");
        this.form.cgstrate = String(rate.cgstrate ?? "");
        this.form.sgstrate = String(rate.sgstrate ?? "");
        this.form.igstrate = String(rate.igstrate ?? "");
        const match = this.gstRates.find((slab) => Number(slab.total) === Number(rate.tax_percent));
        this.selectedGstRate = match ? match.label : "custom";
        this.taxRateLookup = {
          status: "found",
          message: `${rate.code_type} ${rate.code} is active at ${this.form.taxpercent}% tax.`,
        };
        this.errors.hsncode = "";
      } catch (error) {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
        this.taxRateLookup = {
          status: "missing",
          message: error.response?.data?.detail || "No active tax rate found. Add this code in HSN Tax Master first.",
        };
      }
    },

    // ── GST rate quick-apply ───────────────────────────
    applyGstRate(rate) {
      this.selectedGstRate  = rate.label;
      this.form.taxpercent  = rate.total > 0 ? String(rate.total) : "0";
      this.form.cgstrate    = String(rate.cgst);
      this.form.sgstrate    = String(rate.sgst);
      this.form.igstrate    = String(rate.igst);
    },

    // ── Tax field sync ─────────────────────────────────
    syncTaxFromTotal() {
      const total    = parseFloat(this.form.taxpercent) || 0;
      const half     = parseFloat((total / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
      this.form.igstrate = String(total);
    },
    syncTaxFromCgst() {
      const cgst = parseFloat(this.form.cgstrate) || 0;
      const sgst = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromSgst() {
      const cgst = parseFloat(this.form.cgstrate) || 0;
      const sgst = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromIgst() {
      const igst = parseFloat(this.form.igstrate) || 0;
      this.form.taxpercent = String(igst);
      const half = parseFloat((igst / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
    },

    // ── IMAGE METHODS ─────────────────────────────────
    async loadExistingItemImages(itemId) {
      try {
        const response = await axios.get(`/v1/items/${itemId}/images`);
        this.localImages = (response.data || []).map((image) => ({
          ...image,
          url: image.preview_url || image.url,
          isNew: false,
        }));
      } catch (error) {
        console.error("Error loading existing images:", error);
      }
    },

    handleImageUpload(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      const maxSizeBytes = 2 * 1024 * 1024;
      const combinedCount = this.localImages.length + files.length;
      if (combinedCount > this.maxImages) {
        this.uploadError = `You can only upload up to ${this.maxImages} images.`;
        return;
      }

      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          this.uploadError = "Only JPG, JPEG, PNG and WEBP images are allowed.";
          return;
        }
        if (file.size > maxSizeBytes) {
          this.uploadError = "Each image must be 2 MB or smaller.";
          return;
        }
      }

      files.forEach((file) => {
        const url = URL.createObjectURL(file);
        this.localImages.push({ file, url, isNew: true });
      });
      this.uploadError = "";
      event.target.value = null;
    },

    removeLocalImage(index) {
      const image = this.localImages[index];
      if (!image) return;

      if (image.isNew && image.url) {
        URL.revokeObjectURL(image.url);
      }

      if (!image.isNew) {
        this.imagesToDelete.push(image);
      }

      this.localImages.splice(index, 1);
      this.uploadError = "";
    },

    async deleteExistingImages(itemId) {
      if (!this.imagesToDelete.length) return;
      for (const image of this.imagesToDelete) {
        try {
          await axios.delete(`/v1/items/${itemId}/images`, {
            data: {
              image_key: image.image_key || image.url,
            },
          });
        } catch (error) {
          console.error("Failed to delete image:", error);
          throw error;
        }
      }
      this.imagesToDelete = [];
    },

    async uploadNewImages(itemId) {
      const files = this.localImages.filter((img) => img.isNew).map((img) => img.file);
      if (!files.length) return [];
      const formData = new FormData();
      files.forEach((file) => formData.append("image_files", file));
      const response = await axios.post(`/v1/items/${itemId}/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },

    // ── Validation ─────────────────────────────────────
    slugify(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },

    async saveOnlineProduct() {
      try {
        const payload = {
          ...this.ecommerceForm,
          slug: this.ecommerceForm.slug || this.slugify(this.form.itemname),
          online_price: this.ecommerceForm.online_price === "" ? null : Number(this.ecommerceForm.online_price),
        };
        const response = await axios.put(`/v1/items/${this.id}/online`, payload);
        const ecommerce = response.data?.ecommerce || {};
        this.ecommerceImages = Array.isArray(ecommerce.images) ? ecommerce.images : [];
        await Swal.fire({
          title: "Online product saved",
          icon: "success",
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: "Could not save online product",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      }
    },

    async removeFromOnlineStore() {
      try {
        await axios.put(`/v1/items/${this.id}/offline`);
        this.ecommerceForm.is_online = false;
        await Swal.fire({
          title: "Removed from ecommerce store",
          icon: "success",
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: "Could not remove item",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      }
    },

    validate() {
      this.errors = {};
      if (!this.form.itemname.trim())
        this.errors.itemname     = "Item name is required.";
      if (!this.form.itemcode.trim())
        this.errors.itemcode     = "Item code is required.";
      if (!this.form.itemcategory)
        this.errors.itemcategory = "Please select a category.";
      if (!this.form.unit.trim())
        this.errors.unit         = "Unit is required.";
      if (this.form.unitprice === "" || isNaN(this.form.unitprice) || Number(this.form.unitprice) < 0)
        this.errors.unitprice    = "Enter a valid unit price.";
      if (this.form.minstock === "" || isNaN(this.form.minstock) || Number(this.form.minstock) < 0)
        this.errors.minstock     = "Enter a valid minimum stock value.";
      if (this.isTaxableType && !this.form.is_manual_tax_rate && !String(this.form.hsncode || "").trim())
        this.errors.hsncode      = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code is required unless manual tax rate is enabled.`;
      if (this.isTaxableType && !this.form.is_manual_tax_rate && String(this.form.hsncode || "").trim() && !String(this.form.taxpercent || "").trim())
        this.errors.hsncode      = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code must exist in HSN Tax Master.`;
      if (this.isTaxableType && this.form.is_manual_tax_rate && (this.form.taxpercent === "" || isNaN(this.form.taxpercent) || Number(this.form.taxpercent) < 0))
        this.errors.taxpercent   = "Enter a valid manual tax percent.";
      return Object.keys(this.errors).length === 0;
    },

    // ── Submit ─────────────────────────────────────────
    async postData() {
      if (!this.validate()) {
        Swal.fire({
          title: "Please fix the errors",
          text:  "Some required fields are missing or invalid.",
          icon:  "warning",
        });
        this.$nextTick(() => {
          const el = document.querySelector(".is-invalid");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }

      Swal.fire({
        title:              "Save changes?",
        text:               `Update "${this.form.itemname}"?`,
        icon:               "question",
        showCancelButton:   true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor:  "#d33",
        confirmButtonText:  "Yes, save it!",
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        try {
          // Build clean payload — omit empty optional fields
          // NOTE: batchenabled, expiryenabled and image fields excluded (features disabled)
          const payload = {};
          Object.entries(this.form).forEach(([key, val]) => {
            if (val !== "" && val !== null && val !== undefined) {
              payload[key] = val;
            }
          });
          payload.threshold = Number(this.form.minstock || 0);
          payload.maxstock = this.form.maxstock === "" || this.form.maxstock === null ? null : Number(this.form.maxstock);

          await axios.put(`/v1/items/${this.id}`, payload);

          await this.deleteExistingImages(this.id);
          await this.uploadNewImages(this.id);

          await Swal.fire({
            title:             "Changes saved!",
            text:              `"${this.form.itemname}" has been updated.`,
            icon:              "success",
            timer:             1800,
            showConfirmButton: false,
          });
          this.$router.push({ path: "/productlist" });

        } catch (error) {
          console.error(error);
          const msg = error.response?.data?.detail || error.message;
          Swal.fire({ title: "Error", text: `Could not update item: ${msg}`, icon: "error" });
        }
      });
    },
  },

  beforeDestroy() {
    this.localImages.forEach((img) => { if (img?.file && img.url) URL.revokeObjectURL(img.url); });
    this.localImages = [];
  },
};
</script>

<style scoped>
/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.section-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f0f4ff;
  color: #3d5af1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
}
.section-sub {
  font-size: 12px;
  color: #8e92a3;
  margin: 0;
  line-height: 1.4;
}
.section-divider {
  border: none;
  border-top: 1px solid #f0f1f5;
  margin: 24px 0;
}

.tax-rate-status {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.35;
}

.tax-rate-status.loading {
  color: #667085;
}

.tax-rate-status.found {
  color: #117a37;
}

.tax-rate-status.missing {
  color: #d92d20;
}

/* ─────────────────────────────────────────────
   FORM HELPERS
───────────────────────────────────────────── */
.form-label {
  font-size: 13px;
  font-weight: 500;
  color: #3a3f5c;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.required-star { color: #e53935; font-weight: 700; }
.optional-tag {
  font-size: 11px;
  font-weight: 400;
  color: #aaa;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 1px 5px;
}
.form-hint {
  font-size: 11px;
  color: #9ea5b8;
  margin-top: 3px;
  display: block;
}
.stock-warning-hint {
  color: #b25e09;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  padding: 6px 8px;
}

/* ─────────────────────────────────────────────
   INPUT VARIANTS
───────────────────────────────────────────── */
.input-currency { position: relative; }
.input-currency .currency-symbol {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 13px;
  z-index: 1;
  pointer-events: none;
}
.input-currency input { padding-left: 26px !important; }

.input-with-icon { position: relative; }
.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
  font-size: 13px;
  z-index: 1;
}
.input-with-icon input { padding-left: 32px !important; }

.input-with-suffix { position: relative; }
.input-with-suffix input { padding-right: 30px !important; }
.input-suffix {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 13px;
  pointer-events: none;
}

/* ─────────────────────────────────────────────
   TAX PANEL
───────────────────────────────────────────── */
.tax-panel {
  background: #f9faff;
  border: 1px solid #e8ecff;
  border-radius: 10px;
  padding: 20px;
}
.gst-rate-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.gst-rate-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  border: 1.5px solid #dde1f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
  min-width: 80px;
}
.gst-rate-btn:hover  { border-color: #3d5af1; background: #f0f4ff; }
.gst-rate-btn.active { border-color: #3d5af1; background: #3d5af1; color: #fff; }
.rate-label     { font-size: 15px; font-weight: 700; line-height: 1.2; }
.rate-breakdown { font-size: 10px; opacity: 0.75; text-align: center; margin-top: 2px; }
.gst-rate-btn.active .rate-breakdown { opacity: 0.85; }

.gst-breakdown-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.gst-field    { flex: 1; min-width: 100px; max-width: 160px; }
.gst-divider-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10px;
  color: #aaa;
  font-size: 11px;
  gap: 2px;
}
.gst-divider-arrow i { font-size: 14px; color: #3d5af1; }
.gst-plus { font-size: 20px; font-weight: 300; color: #aaa; padding-bottom: 10px; }
.gst-preview {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 8px 14px;
  text-align: center;
  min-width: 100px;
}
.preview-label { font-size: 11px; color: #388e3c; font-weight: 500; }
.preview-value { font-size: 18px; font-weight: 700; color: #2e7d32; }

/* ─────────────────────────────────────────────
   UOM PANEL
───────────────────────────────────────────── */
.uom-panel {
  background: #fffbf2;
  border: 1px solid #ffe9c2;
  border-radius: 10px;
  padding: 20px;
}
.uom-badge-inline {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
  color: #fff;
}
.uom-badge-inline.upper { background: #3d5af1; }
.uom-badge-inline.lower { background: #f57c00; }

.uom-conversion-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}
.uom-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 20px;
  white-space: nowrap;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uom-chip.upper { background: #e8ecff; color: #3d5af1; }
.uom-chip.lower { background: #fff3e0; color: #f57c00; }
.uom-equals { font-size: 18px; color: #aaa; font-weight: 300; flex-shrink: 0; }
.uom-factor-input { width: 70px !important; text-align: center !important; flex-shrink: 0; }
.uom-example-card {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #2e7d32;
}

/* ─────────────────────────────────────────────
   IMAGE GRID — kept for future re-enable
───────────────────────────────────────────── */
.image-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.image-tile {
  width: 100px; height: 100px;
  border-radius: 8px; overflow: hidden;
  position: relative;
  border: 1.5px solid #e0e3ef;
}
.image-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
.image-remove {
  position: absolute; top: 4px; right: 4px;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.9);
  color: #fff; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; padding: 0;
}
.image-status-label {
  position: absolute;
  bottom: 0; left: 0;
  padding: 1px 6px;
  font-size: 10px;
  color: #fff;
  border-top-right-radius: 4px;
  z-index: 5;
}
.image-add-tile {
  width: 100px; height: 100px;
  border-radius: 8px;
  border: 2px dashed #c5cae9;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 6px; cursor: pointer; color: #9ea5b8;
  font-size: 12px; transition: all 0.18s;
}
.image-add-tile:hover { border-color: #3d5af1; color: #3d5af1; background: #f0f4ff; }
.image-add-tile i { font-size: 20px; }

/* ─────────────────────────────────────────────
   ACTION BUTTONS
───────────────────────────────────────────── */
.form-actions { display: flex; gap: 12px; padding-top: 8px; }
.ecommerce-category-select { min-height: 110px; }

/* ─────────────────────────────────────────────
   SKELETON LOADER
───────────────────────────────────────────── */
@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}
.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, #f0f1f5 25%, #e4e5ea 50%, #f0f1f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 6px;
  margin-bottom: 8px;
}
.skeleton-line.w-40 { width: 40%; }

.unit-field-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.unit-field-row :deep(.ui.search.dropdown) {
  flex: 1;
  min-width: 0;
}
.unit-add-btn {
  width: 38px;
  min-width: 38px;
  height: 38px;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  background: #eef2ff;
  color: #4338ca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
}
.unit-add-btn:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.quick-create-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.quick-create-modal {
  width: min(100%, 560px);
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}
.quick-create-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.quick-create-modal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}
.quick-create-modal-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.88rem;
}
.quick-create-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}
</style>
